import React, { useState } from 'react';
import { 
  FileText, 
  CheckCircle, 
  XCircle, 
  Calendar,
  Plus,
  Edit,
  Trash2,
  Eye
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import { useAdminTermsPrivacy } from '../../hooks/useAdminTermsPrivacy';
import { formatDate } from '../../utils/dateUtils';

const AdminTermsPrivacy = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const {
    documents,
    isLoading,
    error,
    filters,
    documentTypeOptions,
    activeStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics: stats
  } = useAdminTermsPrivacy();

  // Define table columns
  const columns = [
    {
      key: 'document_type',
      label: 'Type',
      sortable: true,
      width: 'w-40',
      render: (doc) => (
        <span className="text-sm font-medium text-gray-900 capitalize">
          {doc.document_type?.replace('_', ' ') || 'N/A'}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      width: 'w-64',
      render: (doc) => (
        <p className="text-sm font-medium text-gray-900 truncate">
          {doc.title || 'N/A'}
        </p>
      )
    },
    {
      key: 'content',
      label: 'Content Preview',
      sortable: false,
      width: 'w-96',
      render: (doc) => (
        <p className="text-sm text-gray-900 truncate">
          {doc.content?.substring(0, 100) || 'N/A'}...
        </p>
      )
    },
    {
      key: 'version',
      label: 'Version',
      sortable: true,
      width: 'w-24',
      render: (doc) => (
        <span className="text-sm font-medium text-gray-900">
          {doc.version || '1.0'}
        </span>
      )
    },
    {
      key: 'effective_date',
      label: 'Effective Date',
      sortable: true,
      width: 'w-32',
      render: (doc) => (
        doc.effective_date ? (
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900">
                {formatDate(doc.effective_date, 'short')}
              </div>
            </div>
          </div>
        ) : (
          <span className="text-sm text-gray-400">N/A</span>
        )
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 'w-32',
      render: (doc) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(doc.created_at, 'medium')}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      width: 'w-24',
      render: (doc) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          doc.is_active 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {doc.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'View',
      icon: Eye,
      variant: 'default',
      onClick: (doc) => {
        console.log('View document:', doc);
      }
    },
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: (doc) => {
        setEditingDocument(doc);
        setShowCreateModal(true);
      }
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (doc) => {
        if (window.confirm('Are you sure you want to delete this document?')) {
          // deleteDocument(doc.id);
        }
      }
    }
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Documents</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load documents. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6" />
                Terms & Privacy
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage terms of service, privacy policies, and legal documents
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingDocument(null);
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Document
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Documents</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_documents || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.active_documents || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SearchSelect
              label="Document Type"
              value={filters.document_type}
              onChange={(value) => handleFilterChange('document_type', value)}
              options={documentTypeOptions}
            />
            <SearchSelect
              label="Status"
              value={filters.is_active}
              onChange={(value) => handleFilterChange('is_active', value)}
              options={activeStatusOptions}
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <DataTable
            data={documents}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search by title, content, or document type..."
            onSearch={handleSearch}
            emptyMessage="No documents found"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminTermsPrivacy;


